import React from 'react'
import KebabVerticalIcons from '@/icons/KebabVerticalIcons'
import StarIcons from '@/icons/StarIcons'
import { Checkbox } from '@/components/ui/checkbox'
import { t } from 'i18next'

interface FileRowProps {
    filename: string
    isStarred: boolean
    icon: React.JSX.Element
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void
    isSelected?: boolean
    isSelectedMode?: boolean
    size?: number
    isFolder?: boolean
    lastModified?: string
}

// Helper function to format file size
const formatFileSize = (bytes?: number): string => {
    if (!bytes || bytes === 0) return '--'

    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

// Helper function to format date with time
const formatDate = (dateString?: string): string => {
    if (!dateString) return '--'

    try {
        const date = new Date(dateString)
        const now = new Date()
        const diff = now.getTime() - date.getTime()
        const daysDiff = Math.floor(diff / (1000 * 60 * 60 * 24))

        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })

        // If today, show "Today" with time
        if (daysDiff === 0) {
            return `${t('today')}, ${timeStr}`
        }
        // If yesterday, show "Yesterday" with time
        if (daysDiff === 1) {
            return `${t('yesterday')}, ${timeStr}`
        }
        // If within this year, show month, day and time
        if (date.getFullYear() === now.getFullYear()) {
            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            })
            return `${dateStr}, ${timeStr}`
        }
        // Otherwise show full date with time
        const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
        return `${dateStr}, ${timeStr}`
    } catch (e) {
        console.error(e)
        return '--'
    }
}

const FileRow = React.forwardRef<HTMLDivElement, FileRowProps>(
    (
        {
            filename,
            isStarred,
            icon,
            onClick,
            isSelected = false,
            isSelectedMode = false,
            size,
            isFolder = false,
            lastModified,
        },
        ref
    ) => (
        <div
            ref={ref}
            className={`flex flex-row items-center gap-4 py-2 px-3 relative cursor-pointer ${
                isSelected && isSelectedMode
                    ? 'bg-blue-100 border-2 border-blue-400 hover:bg-blue-200'
                    : 'hover:bg-gray-50'
            }`}
            data-file-row
            onClick={onClick}
            onContextMenu={onClick}
        >
            {isSelectedMode && (
                <div className="size-5 shrink-0">
                    <Checkbox
                        className="data-[state=checked]:border-white data-[state=checked]:text-white"
                        checked={isSelected}
                    />
                </div>
            )}
            <div className="size-8 shrink-0">{icon}</div>
            <div className="flex-1 min-w-0 flex items-center gap-2">
                <div className="text-sm font-medium truncate">{filename}</div>
                {isStarred && (
                    <div className="size-4 shrink-0">
                        <StarIcons className="text-[#FFD600]" />
                    </div>
                )}
            </div>

            {/* File size column - hidden on mobile */}
            <div className="hidden sm:block w-20 flex-shrink-0 text-right">
                <span className="text-sm text-gray-500">
                    {isFolder ? '--' : formatFileSize(size)}
                </span>
            </div>

            {/* Last modified column - hidden on mobile */}
            <div className="hidden sm:block w-40 flex-shrink-0 text-right">
                <span className="text-sm text-gray-500">
                    {formatDate(lastModified)}
                </span>
            </div>

            <div
                className="w-16 flex-shrink-0 flex justify-end"
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    const parent = (e.currentTarget as HTMLElement).closest(
                        '[data-file-row]'
                    ) as HTMLElement | null
                    if (parent) {
                        parent.dispatchEvent(
                            new MouseEvent('contextmenu', {
                                bubbles: true,
                                clientX: (e as React.MouseEvent).clientX,
                                clientY: (e as React.MouseEvent).clientY,
                            })
                        )
                    }
                }}
            >
                <KebabVerticalIcons className="text-gray-500 size-5 hover:text-gray-700 hover:scale-110 cursor-pointer" />
            </div>
        </div>
    )
)

export default FileRow
